// @flow
import React, { useState, useEffect, useCallback } from 'react';
import { throttle } from 'lodash';
import { withApollo, WithApolloClient } from 'react-apollo';
import styled from 'styled-components';
import InlineEdit from '@atlaskit/inline-edit';
import SectionMessage from '@atlaskit/section-message';
import { useMutation, useQuery } from '@apollo/react-hooks';
import Spinner from '@atlaskit/spinner';
import { SimpleTag } from '@atlaskit/tag';
import TagGroup from '@atlaskit/tag-group';
import InlineMessage from '@atlaskit/inline-message';
import { UPDATE_JIRA_ISSUE_VERSIONS } from '../../graphql/mutations';
import {
    HAS_ACCESS_COMPONENT_VERSIONS_CUSTOM_FIELDS,
    JIRA_ISSUE_LINK_COMPONENTS_VERSIONS,
} from '../../graphql/queries';
import { analytics } from '../../services/analytics';
import Frame from '../../services/iframe';
import { ErrorParser } from '../../utils/ErrorParser';
import VirtualComponentVersionSelect from '../select/VirtualComponentVersionSelect';
import useJiraContext from '../../hooks/useJiraContext';

const throttledFrameAdjuster = throttle(Frame.adjustFrameHeight, 100, { leading: true });

const Wrapper = styled.div`
    display: flex;
    margin: 0px 1px;
    form {
        margin-top: -7px !important;
        width: 100%;
    }
    .sc-esjQYD {
        width: 100%;
    }
    flex-direction: column;
    flex-gap: 10px;
    /* InlineEdit action buttons are position:absolute by default; body overflow-y:hidden clips them in iframe. Put them in flow. */
    form > div > div > div:last-child {
        position: relative !important;
        justify-content: flex-end;
    }
`;

const DefaultView = styled.div`
    word-break: break-word;
    ${({ $withPadding }) => ($withPadding ? `padding: 8px 0 8px 0;` : ``)}
`;

const NoneView = styled.span`
    color: #5e6c84;
`;

const Loading = () => {
    return (
        <div style={{ padding: '7px' }}>
            <Spinner size="small" />
        </div>
    );
};

const LinkComponentVersions = ({ client }: WithApolloClient) => {
    const { issueId, projectId } = useJiraContext();
    const [hasAccessCpVersionsCustomFields, setHasAccessComponentVersionsCustomFields] = useState(true);
    const [issueVersions, setIssueVersions] = useState();

    useEffect(() => {
        const observer = new MutationObserver(throttledFrameAdjuster);
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
        return () => observer.disconnect();
    }, []);

    const jiraIssueLinkComponentsVersionsQuery = useQuery(JIRA_ISSUE_LINK_COMPONENTS_VERSIONS, {
        skip: !issueId,
        variables: { id: issueId },
        onCompleted: (result) => {
            if (result?.jiraIssueWithComponentVersions) {
                setIssueVersions(
                    result.jiraIssueWithComponentVersions?.map(({ id, name }) => ({ label: name, value: id })) ?? [],
                );
            }
        },
    });

    const jiraIssueHasAccessCustomFieldsQuery = useQuery(HAS_ACCESS_COMPONENT_VERSIONS_CUSTOM_FIELDS, {
        skip: !issueId,
        variables: { id: issueId },
        onCompleted: (result) => {
            if (result?.hasAccessComponentVersionsCustomFields) {
                setHasAccessComponentVersionsCustomFields(result.hasAccessComponentVersionsCustomFields.hasAccess);
            }
        },
    });

    const [updateJiraIssueVersions, { error: updateJiraIssueVersionsError }] = useMutation(UPDATE_JIRA_ISSUE_VERSIONS, {
        onCompleted: () => {
            if (issueId) {
                client.writeQuery({
                    query: JIRA_ISSUE_LINK_COMPONENTS_VERSIONS,
                    variables: { id: issueId },
                    data: {
                        jiraIssueWithComponentVersions: (issueVersions || []).map(({ value, label }) => ({
                            id: value,
                            name: label,
                            __typename: 'IssueVersion',
                        })),
                    },
                });
            }
        },
    });
    const onConfirm = useCallback(() => {
        if (!issueId) {
            return;
        }
        const current = (jiraIssueLinkComponentsVersionsQuery.data?.jiraIssueWithComponentVersions ?? [])
            .map(({ id }) => id)
            .sort();
        const next = (issueVersions ?? []).map(({ value }) => value).sort();
        if (current.join(',') === next.join(',')) {
            return;
        }
        updateJiraIssueVersions({
            variables: {
                id: Number(issueId),
                versions: next,
            },
        }).then(() => {
            if (issueVersions.length) {
                analytics.track({
                    userId: window.license,
                    event: 'linked_componet_version_issue',
                    timestamp: new Date(),
                    properties: {
                        addon_key: process.env.REACT_APP_KEY,
                    },
                });
            }
        });
    }, [
        issueId,
        issueVersions,
        jiraIssueLinkComponentsVersionsQuery.data?.jiraIssueWithComponentVersions,
        updateJiraIssueVersions,
    ]);

    const onChange = useCallback((selected) => setIssueVersions(selected), []);
    const onCancel = useCallback(() => {
        setIssueVersions(
            jiraIssueLinkComponentsVersionsQuery.data?.jiraIssueWithComponentVersions?.map(({ id, name }) => ({
                label: name,
                value: id,
            })) ?? [],
        );
    }, [jiraIssueLinkComponentsVersionsQuery.data?.jiraIssueWithComponentVersions]);

    const readView = useCallback(
        () => (
            <DefaultView $withPadding={!issueVersions?.length} className="link-components-view">
                {issueVersions?.length ? (
                    <TagGroup>
                        {issueVersions.map((component) => (
                            <SimpleTag key={component.value} text={component.label} />
                        ))}
                    </TagGroup>
                ) : (
                    <NoneView>Click to select component versions</NoneView>
                )}
            </DefaultView>
        ),
        [issueVersions],
    );

    const editView = useCallback(
        (fieldProps) => (
            <VirtualComponentVersionSelect
                {...fieldProps}
                className="link-components-select"
                classNamePrefix="link-components-select"
                isMulti
                autoFocus
                openMenuOnFocus
                closeMenuOnSelect={false}
                value={issueVersions}
                projectId={projectId}
                onChange={onChange}
                menuInDocumentFlow
            />
        ),
        [issueVersions, onChange, projectId],
    );

    if (
        !projectId ||
        !issueId ||
        jiraIssueLinkComponentsVersionsQuery.loading ||
        jiraIssueHasAccessCustomFieldsQuery.loading
    ) {
        return <Loading />;
    }

    if (jiraIssueLinkComponentsVersionsQuery.error) {
        return `Error! ${jiraIssueLinkComponentsVersionsQuery.error.message}`;
    }

    if (jiraIssueHasAccessCustomFieldsQuery.error) {
        return `Error! ${jiraIssueHasAccessCustomFieldsQuery.error.message}`;
    }

    if (updateJiraIssueVersionsError) {
        return (
            <InlineMessage type="error" placement="right">
                <p>{ErrorParser.parse(updateJiraIssueVersionsError.message) || 'Please try again later'}</p>
            </InlineMessage>
        );
    }

    return (
        <Wrapper id="link-component-wrapper">
            <InlineEdit
                className="link-components-inline-edit"
                editView={editView}
                readView={readView}
                readViewFitContainerWidth
                onConfirm={onConfirm}
                onCancel={onCancel}
                isEditing={hasAccessCpVersionsCustomFields ? undefined : false}
            />
            {!hasAccessCpVersionsCustomFields && (
                <SectionMessage>
                    <p>{`To use this feature, the custom fields "Component versions", "Released component versions", and "Unreleased component versions" must be available in this project. Please associate the fields with the appropriate project or screens.`}</p>
                </SectionMessage>
            )}
        </Wrapper>
    );
};

export default withApollo(LinkComponentVersions);
